import { createSlice } from '@reduxjs/toolkit';
import api from 'api';
import formErrorsHandle from '../../../utils/formErrorsHandle';
import toast from 'react-hot-toast';
import { mergeRolesToSingleRole } from '../../../utils/mergeRoles';

const initialState = {
  loading: false,
  companyList: [],
  userCompaniesList: []
};
const company = createSlice({
  name: 'company',
  initialState,
  reducers: {
    setLoading(state, action) {
      return {
        ...state,
        loading: action.payload
      };
    },
    getList(state, action) {
      return {
        ...state,
        companyList: action.payload?.data?.result,
        count: action.payload?.data?.count
      };
    },
    getUserCompaniesList(state, action) {
      return {
        ...state,
        userCompaniesList: action.payload?.data
      };
    },
    getById(state, action) {
      return {
        ...state,
        companyInfo: action.payload.data
      };
    },
    // The signed-in user's own company timezone. Kept apart from companyInfo,
    // which also holds whichever company a system admin is editing.
    setOwnTimeZone(state, action) {
      return {
        ...state,
        ownTimeZone: action.payload
      };
    }
  }
});

export const companyActions = company.actions;

export const getCompanyListAction = (data) => async (dispatch) => {
  dispatch(companyActions.setLoading(true));
  try {
    const res = await api.post(`api/CompanyManagement/Filter`, data);
    if (res.status === 200 && res?.data) {
      dispatch(companyActions.getList(res.data));
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  } finally {
    dispatch(companyActions.setLoading(false));
  }
};

export const switchCompanyAction = (companyId) => async (dispatch) => {
  dispatch(companyActions.setLoading(true));
  try {
    const res = await api.post(`api/UserCompany/switch-company`, null, {
      params: { companyId }
    });
    if (res.status === 200 && res && res.data) {
      const respData = res.data?.data;
      const roles = mergeRolesToSingleRole(respData.roles);
      localStorage.setItem('token', respData.token);
      localStorage.setItem('user', JSON.stringify(respData));
      localStorage.setItem('role', JSON.stringify(roles));
      localStorage.setItem('isAuthenticated', 'true');
      return Promise.resolve(respData);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  } finally {
    dispatch(companyActions.setLoading(false));
  }
};

export const getUserCompaniesAction = () => async (dispatch) => {
  dispatch(companyActions.setLoading(true));
  try {
    const res = await api.get(`api/UserCompany`);
    if (res.status === 200 && res?.data) {
      dispatch(companyActions.getUserCompaniesList(res.data));
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  } finally {
    dispatch(companyActions.setLoading(false));
  }
};

export const editCompanyInfo = (data) => async () => {
  try {
    const res = await api.put(`api/company`, data);
    if (res.status === 200 && res?.data) {
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    return Promise.reject(errors.response?.data);
  }
};

export const getCompanyById = (id) => async (dispatch) => {
  dispatch(companyActions.setLoading(true));
  try {
    const res = await api.get(`api/company`, { params: { id } });
    if (res.status === 200 && res?.data) {
      dispatch(companyActions.getById(res.data));
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  } finally {
    dispatch(companyActions.setLoading(false));
  }
};
export const getOwnCompany = () => async (dispatch) => {
  dispatch(companyActions.setLoading(true));
  try {
    const res = await api.get(`api/company/own`);
    if (res.status === 200 && res?.data) {
      dispatch(companyActions.getById(res.data));
      dispatch(companyActions.setOwnTimeZone(res.data?.data?.timeZone || null));
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    return Promise.reject(errors.response?.data);
  } finally {
    dispatch(companyActions.setLoading(false));
  }
};
export const verifyCompanyEmail = (data, navigate) => async () => {
  try {
    const res = await api.post(`api/Company/confirm-company-email`, data, {
      params: data
    });
    if (res.status === 200 && res?.data) {
      navigate('/');
      toast.success('Verification successfully!!!');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};
export const resendEmailVerifyLink = (data) => async () => {
  try {
    const res = await api.post(`api/Company/resend-verification`, data);
    if (res.status === 200 && res?.data) {
      toast.success('Email send successfully!!!');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};

export const clearCompanyById = () => async (dispatch) => {
  dispatch(companyActions.getById(''));
};

export const createCompany = (data) => async (dispatch) => {
  try {
    const res = await api.post(`api/CompanyManagement`, data);
    if (res.status === 200 && res?.data) {
      toast.success('Company created successfully!');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    return Promise.reject(errors.response?.data);
  }
};

export const updateCompany = (id, data) => async (dispatch) => {
  try {
    const res = await api.put(`api/CompanyManagement/${id}`, { ...data, id });
    if (res.status === 200 && res?.data) {
      toast.success('Company updated successfully!');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    return Promise.reject(errors.response?.data);
  }
};

export const toggleCompanyActivation = (id, isActive, refreshCallback) => async (dispatch) => {
  try {
    const res = await api.put(`api/CompanyManagement/Activation/${id}`, { isActive });
    if (res.status === 200) {
      toast.success(`Company ${isActive ? 'activated' : 'deactivated'} successfully!`);
      if (refreshCallback) {
        refreshCallback();
      }
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};

export const resendCompanyInvite = (companyId) => async () => {
  try {
    const res = await api.post(`api/CompanyManagement/${companyId}/ResendInvite`);
    if (res.status === 200) {
      toast.success('Invitation email resent successfully!');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};

export const savePaymentCredentials = (companyId, data) => async (dispatch) => {
  try {
    const res = await api.post(`api/CompanyManagement/Credentials/${companyId}`, data);
    if (res.status === 200 && res?.data) {
      toast.success('Payment credentials saved successfully!');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    return Promise.reject(errors.response?.data);
  }
};

export const getPaymentCredentials = (companyId) => async (dispatch) => {
  try {
    const res = await api.get(`api/CompanyManagement/Credentials/${companyId}`);
    if (res.status === 200 && res?.data) {
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    return Promise.reject(errors.response?.data);
  }
};

export const getCompanyByIdForEdit = (id) => async (dispatch) => {
  dispatch(companyActions.setLoading(true));
  try {
    const res = await api.get(`api/CompanyManagement/${id}`);
    if (res.status === 200 && res?.data) {
      dispatch(companyActions.getById(res.data));
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  } finally {
    dispatch(companyActions.setLoading(false));
  }
};

export default company.reducer;
